import {useCallback, useEffect, useMemo, useState} from "react";
import {getPostsByCategoryId} from "../api/getPostsByCategoryId";
import {Category, Post, TreeNode} from "../../../types/Post";
import {buildBreadcrumbFromTree, buildTreeFromFlat} from "../../../utils/category";
import {getPosts} from "../api/getPosts";
import {getCategories} from "../../../services/category";

export const usePostList = ({ categoryId }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryTree, setCategoryTree] = useState<TreeNode[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const fetchPosts = useCallback(async () => {
    if (categoryId) {
      const {posts: tempPosts} = await getPostsByCategoryId(categoryId);
      return tempPosts;
    }
    return await getPosts();
  }, [categoryId])

  const fetchCategories = useCallback(async () => {
    return await getCategories();
  }, [])

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try{
      const [postData, categoryData] = await Promise.all([
        fetchPosts(),
        fetchCategories(),
      ]);
      setPosts(postData);
      setCategories(categoryData);
      setCategoryTree(buildTreeFromFlat(categoryData));
    } catch (error) {
      setError(error?.message || 'Failed to fetch ');
    } finally {
      setIsLoading(false);
    }
  }, [fetchPosts, fetchCategories]);

  useEffect(() => {
    fetch();
  }, []);

  const breadCrumb = useMemo(
    () => buildBreadcrumbFromTree(categoryTree, categoryId),
    [categoryTree, categoryId]
  )

  const nextLevelCategories = useMemo(() => {
    const current = categories.find((c) => c.id === categoryId);
    if (!current) return categories.filter((c) => c.level === 1);
    return categories.filter((c) => c.parentId === categoryId);
  }, [categories, categoryId]);

  return { posts, categoryTree, breadCrumb, nextLevelCategories, isLoading };

}
